import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Document, Issue } from '../core/types';

interface Props {
  document: Document;
  issues: Issue[];
  onClose: () => void;
}

const docIcon = (type: Document['type']): string => {
  if (type === 'passport') return '🛂';
  if (type === 'statement') return '📝';
  return '📎';
};

// 判断某个字段是否被 issues 描述提及
function isSuspiciousField(key: string, value: string, issues: Issue[]): boolean {
  return issues.some(issue =>
    issue.description.includes(key) || issue.description.includes(value)
  );
}

const DocumentModal: React.FC<Props> = ({ document, issues, onClose }) => {
  const { t } = useTranslation();
  const [markedFields, setMarkedFields] = useState<Set<string>>(new Set());

  const toggleMark = (key: string) => {
    setMarkedFields(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  return (
    <div className="doc-modal-overlay" onClick={onClose}>
      <div className="doc-modal" onClick={e => e.stopPropagation()}>
        <div className="doc-modal-header">
          <h2>
            <span className="document-icon">{docIcon(document.type)}</span>
            {document.title}
          </h2>
          <button className="doc-modal-close" onClick={onClose}>✕</button>
        </div>

        <div className="doc-modal-content">
          {Object.entries(document.content).map(([key, value]) => {
            const autoFlag = isSuspiciousField(key, value, issues);
            const manualFlag = markedFields.has(key);
            const flagged = autoFlag || manualFlag;
            return (
              <div
                key={key}
                className={`doc-field-row ${flagged ? 'flagged' : ''} ${manualFlag ? 'manual' : ''}`}
                onClick={() => toggleMark(key)}
                title={t('documents.clickToMark')}
              >
                <span className="doc-field-key">{key}</span>
                <span className="doc-field-value">{value}</span>
                {autoFlag && <span className="doc-flag-icon">⚠️</span>}
                {manualFlag && !autoFlag && <span className="doc-flag-icon">🔍</span>}
              </div>
            );
          })}
        </div>

        {issues.length > 0 && (
          <div className="doc-modal-issues">
            <p className="doc-issues-title">⚠️ {t('documents.relatedIssues')}</p>
            {issues.map((issue, i) => (
              <p key={i} className={`doc-issue-item severity-${issue.severity}`}>
                • {issue.description}
              </p>
            ))}
          </div>
        )}

        <p className="doc-modal-hint">{t('documents.clickFieldHint')}</p>
      </div>
    </div>
  );
};

export default DocumentModal;
